// Базовая обёртка экрана: status-bar + header (back-btn + title + ?) + content.
// Кнопка «?» переключает глобальный «Режим изучения» (Этап 46.13):
//   - подсвечивает [data-tt] элементы на экране
//   - тап по элементу открывает подсказку
//   - снова «?» = выход из режима

import { useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBackButton, useTelegram } from '../telegram/useTelegram';
import { useStudyMode } from '../study/useStudyMode';
import styles from './ScreenLayout.module.css';

interface Props {
  title: string;
  backTo?: string;        // если задан — кнопка «назад» уходит на этот path
  backLabel?: string;     // дефолт «назад»
  rightSlot?: ReactNode;  // например иконка-поиска (Friends), фильтр и т.п.
  // Показать ли кнопку «?» в шапке. По умолчанию true.
  showHelp?: boolean;
  // Переопределить поведение «?»: вместо toggleStudy() вызвать этот колбэк.
  // Удобно для экранов где нужно показать одну большую справку (БП, Достижения)
  // — а не подсвечивать data-tt элементы.
  onHelpOverride?: () => void;
  children: ReactNode;
}

export function ScreenLayout({
  title, backTo, backLabel = 'назад', rightSlot, showHelp = true, onHelpOverride, children
}: Props) {
  const navigate = useNavigate();
  const { hapticTap } = useTelegram();
  const toggleStudy = useStudyMode(s => s.toggle);
  const studyActive = useStudyMode(s => s.active);

  const handleBack = useCallback(() => {
    if (studyActive) {
      // В режиме изучения «назад» сначала закрывает режим, не уводит с экрана.
      hapticTap();
      toggleStudy();
      return;
    }
    hapticTap();
    if (backTo) navigate(backTo);
    else navigate(-1);
  }, [navigate, backTo, hapticTap, studyActive, toggleStudy]);

  // Telegram BackButton — синхронизирован с навигацией экрана.
  useBackButton(handleBack, true);

  const helpBtn = showHelp && !rightSlot ? (
    <button
      className={`${styles.helpBtn} ${studyActive ? styles.helpBtnActive : ''}`}
      aria-label="Режим изучения"
      data-tt-exit
      onClick={(e) => {
        e.stopPropagation();
        hapticTap();
        if (onHelpOverride) onHelpOverride();
        else toggleStudy();
      }}
    >?</button>
  ) : null;

  return (
    <div className={styles.screen}>
      <div className={styles.content}>
        <div className={styles.header}>
          <button className={`${styles.backBtn} cb`} onClick={handleBack}>
            <span className={styles.chev}>‹</span> {backLabel}
          </button>
          <div className={`${styles.title} cd`}>{title}</div>
          {rightSlot || helpBtn || <div className={styles.rightSpacer} />}
        </div>
        <div className={styles.scroll}>{children}</div>
      </div>
    </div>
  );
}
